import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  Copy,
  Loader2
} from "lucide-react";
import { UserRole } from "@/lib/schema";
import { auth } from "@/lib/firebase";
import { generateSecurePassword } from "@/lib/userUtils";
import { callAuthenticatedFunction } from "@/lib/authUtils";

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated: () => void;
}

export const CreateUserModal = ({ 
  open, 
  onOpenChange, 
  onUserCreated 
}: CreateUserModalProps) => {
  const { toast } = useToast();
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: UserRole.RECRUITER as UserRole
  });
  const [generatedPassword, setGeneratedPassword] = useState("");

  const handleCloseModal = () => {
    setNewUser({
      firstName: "",
      lastName: "",
      email: "",
      role: UserRole.RECRUITER
    });
    setGeneratedPassword("");
    onOpenChange(false);
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Missing Information",
        description: "First name, last name, and email are required.",
        variant: "destructive"
      });
      return;
    }

    if (!auth.currentUser) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Authentication Required",
        description: "You must be logged in to create users.",
        variant: "destructive"
      });
      return;
    }

    setCreatingUser(true);
    
    try {
      const autoPassword = generateSecurePassword();
      
      const userData = {
        email: newUser.email,
        password: autoPassword,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        emailVerified: false,
        disabled: false
      };

      await callAuthenticatedFunction('createUser', userData);

      setGeneratedPassword(autoPassword);

      toast({
        title: "User Created Successfully", 
        description: `${newUser.firstName} ${newUser.lastName} has been created. Password: ${autoPassword}`
      });

      onUserCreated(); // Refresh the user list
    } catch (error: any) {
      let errorMessage = "Failed to create user. Please try again.";
      
      if (error.message && error.message.includes("Response is missing data field")) {
        errorMessage = "Function completed but returned invalid response format.";
      } else if (error.code && error.message) {
        errorMessage = error.message;
        
        switch (error.code) {
          case 'functions/already-exists':
            errorMessage = "The email address is already in use by another account.";
            break;
          case 'functions/permission-denied':
            errorMessage = "You don't have permission to create users.";
            break;
          case 'functions/unauthenticated':
            errorMessage = "You must be authenticated to create users.";
            break;
          case 'functions/invalid-argument':
            errorMessage = "Invalid user data provided. Please check all required fields.";
            break;
          case 'functions/internal':
            errorMessage = "An internal server error occurred. Please try again.";
            break;
          case 'functions/unavailable':
            errorMessage = "Service temporarily unavailable. Please try again later.";
            break;
          case 'functions/deadline-exceeded':
            errorMessage = "Request timed out. Please try again.";
            break;
          case 'functions/cancelled':
            errorMessage = "Operation was cancelled.";
            break;
          case 'functions/not-found':
            errorMessage = "Service not found.";
            break;
          case 'functions/resource-exhausted':
            errorMessage = "Service temporarily overloaded. Please try again later.";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Error Creating User",
        description: errorMessage,
        variant: "destructive"
      });
      
      setGeneratedPassword("");
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleCloseModal}>
      <DialogContent 
        className="max-w-md overflow-y-auto scrollbar-hide dialog-content-wrapper"
        onPointerDownOutside={(e) => {
          // Allow clicks on Radix Select components (which render in portals)
          const target = e.target as HTMLElement;
          if (
            target.closest('[data-radix-select-content]') ||
            target.closest('[data-radix-select-viewport]') ||
            target.closest('[data-radix-select-item]') ||
            target.closest('[data-radix-portal]')
          ) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Create a user invitation. A secure temporary password will be generated for initial access.
          </DialogDescription>
        </DialogHeader>
        <style>{`
          /* Force z-index and pointer events for Select components */
          [data-radix-select-content],
          [data-radix-select-viewport],
          [data-radix-select-item],
          [data-radix-portal] {
            z-index: 200 !important;
            pointer-events: auto !important;
          }

          /* Prevent scroll on Select trigger focus */
          .dialog-content-wrapper [data-radix-select-trigger],
          .dialog-content-wrapper [data-radix-select-trigger]:focus,
          .dialog-content-wrapper [data-radix-select-trigger]:focus-visible {
            scroll-margin: 0 !important;
            scroll-padding: 0 !important;
            scroll-behavior: auto !important;
          }
        `}</style>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input
                value={newUser.firstName}
                onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="John"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input
                value={newUser.lastName}
                onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Doe"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john@company.com"
              className="mt-1"
            />
          </div>

          {/* Show generated password after user creation */}
          {generatedPassword && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <Label className="text-green-800 font-semibold">Temporary Password Generated</Label>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="text"
                  value={generatedPassword}
                  readOnly
                  className="font-mono text-sm bg-white"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedPassword);
                    toast({
                      title: "Password Copied",
                      description: "Temporary password copied to clipboard."
                    });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-green-700 mt-1">
                This is a temporary password. Share it securely with the user.
              </p>
            </div>
          )}

          <div>
            <Label>Role *</Label>
            <Select
              value={newUser.role}
              onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value as UserRole }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className="z-[200]"
                position="popper"
                sideOffset={5}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <SelectItem value={UserRole.RECRUITER}>Recruiter</SelectItem>
                <SelectItem value={UserRole.SUPER_ADMIN}>Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCloseModal}
              disabled={creatingUser}
            >
              {generatedPassword ? "Close" : "Cancel"}
            </Button>
            {!generatedPassword && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCreateUser}
                disabled={creatingUser || !newUser.firstName || !newUser.lastName || !newUser.email}
              >
                {creatingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {creatingUser ? "Creating..." : "Create User"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
